import { Document } from 'react-pdf';
import { VariableSizeList as List } from 'react-window';
import styled, { css } from 'styled-components';

import { ChooseLanguageV2 } from '@/_pages/interwill/components/ChooseLanguageV2';
import { media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';

export const Wrapper = styled.div`
  --sb-track-color: rgba(227, 227, 227, 0.5);
  --sb-thumb-color: #e3e3e3;
  --sb-size: 0;
  width: 100%;
  height: inherit;
  background: ${COLORS.colorFilePreviewBg};
  display: flex;
  align-items: center;
  justify-content: center;

  .react-pdf__message--loading {
    width: 100%;
  }

  *::-webkit-scrollbar {
    width: var(--sb-size);
  }

  * {
    &::-webkit-scrollbar-track {
      visibility: hidden;
      background: var(--sb-track-color);
      border-radius: 2px;
    }

    &::-webkit-scrollbar-thumb {
      visibility: hidden;
      background: var(--sb-thumb-color);
      border-radius: 2px;
    }

    &:hover {
      &::-webkit-scrollbar-track,
      &::-webkit-scrollbar-thumb {
        visibility: visible;
      }
    }
  }
  @media (max-width: 1360px) {
    padding: 16px;
  }

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

export const Row = styled.div<{ $hidden?: boolean }>`
  display: flex;
  align-items: center;
  gap: 32px;
  font-size: 14px;

  @media (max-width: 800px) {
    flex-direction: row-reverse;
    transition: all 0.5s;
    max-height: 44px;
    gap: 24px;

    ${({ $hidden }) =>
      $hidden &&
      css`
        max-height: 0;
        & > div {
          opacity: 0 !important;
        }
      `}
  }
`;

export const ButtonWrapper = styled.div`
  display: none;

  @media screen and (max-width: 768px) {
    display: block;
  }
`;

export const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;

  .text {
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    color: ${COLORS.white};
  }

  .icon {
    color: ${COLORS.white};
    font-size: 20px;
  }
  .icon::before {
    color: ${COLORS.white};
  }
  @media (max-width: 800px) {
    transition: all 0.5s;

    &:nth-of-type(2) {
      order: -1;
    }

    .text {
      display: none;
    }
  }
`;

export const IconWrapperText = styled.span`
  @media (max-width: 768px) {
    display: none;
  }
`;

export const Pages = styled.div<{ $rendered?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-height: 100%;
  height: 100%;
  overflow: auto;
  flex-shrink: 0;

  ${({ $rendered }) =>
    !$rendered &&
    css`
      position: absolute;
      visibility: hidden;
    `}
`;

export const ImagePage = styled.div`
  height: 100%;
  overflow: auto;
`;

export const ImageSidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: fit-content;
  max-width: var(--thumbnailWidth);
  max-height: 100%;
  height: 100%;
  overflow: auto;
  overflow-x: hidden;
  min-width: calc(var(--thumbnailWidth) + 12px);
`;

export const DocxThumbnail = styled.div`
  user-select: none;
  background: ${COLORS.white};
`;

export const DocxWrapper = styled.div`
  overflow: auto;
  width: 100%;
`;

export const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: fit-content;
  max-height: 100%;
  height: 100%;
  overflow: auto;
  overflow-x: hidden;
  min-width: calc(var(--thumbnailWidth) + 12px);
  position: absolute;
  visibility: hidden;

  &.rendered {
    position: relative;
    visibility: visible;
  }

  @media (max-width: 1360px) {
    padding-right: 0;
    transition: all 0.5s;
    overflow: hidden;
    opacity: 0;
    margin-left: -24px;
    min-width: 0;
  }
`;

export const InnerWrapper = styled.div`
  padding: 24px 120px 24px 24px;
  max-height: 100%;
  height: 100%;
  position: relative;
  min-width: 100%;
  display: flex;
  flex-direction: column;

  @media (max-width: 1360px) {
    padding: 0;
  }
`;

export const Header = styled.div`
  color: ${COLORS.white};
  padding-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  font-size: 14px;

  ${media.mobile} {
    padding-bottom: 0;
  }
`;

export const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 1360px) {
    padding-right: 64px;
  }

  @media (max-width: 991px) {
    position: relative;
    padding-right: 64px;
    padding-bottom: 0;
  }

  @media (max-width: 768px) {
    padding-right: 0;
  }
`;

export const ImageWrapper = styled.div`
  display: flex;
  height: 100%;
  max-height: calc(100% - 50px);
  color: ${COLORS.white};
  width: 100%;
  gap: 100px;
  overflow-x: hidden;
  overflow-y: auto;
  flex-grow: 1;

  @media (max-width: 1360px) {
    padding: 0;
    gap: 24px;
    max-height: 100%;
    margin-top: 40px;
    transition: all 0.25s;
    gap: 24px;
  }

  ${media.mobile} {
    margin-top: 24px;
    overflow-x: auto;
  }
`;

export const Image = styled.img`
  object-fit: contain;
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
`;

export const DocumentWrapper = styled(Document)<{ $hidden?: boolean }>`
  display: flex;
  height: 100%;
  max-height: calc(100% - 50px);
  color: ${COLORS.white};
  width: 100%;
  gap: 100px;
  overflow-x: hidden;
  overflow-y: auto;
  flex-grow: 1;

  @media (max-width: 1360px) {
    padding: 0;
    gap: 24px;
    max-height: 100%;
    margin-top: 40px;
    transition: all 0.25s;
    gap: 24px;

    ${({ $hidden }) =>
      $hidden &&
      css`
        max-height: calc(100% - 120px + 44px);
        margin-top: 16px;
      `}
  }

  ${media.mobile} {
    margin-top: 24px;
    overflow-x: auto;
  }
`;

export const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  color: ${COLORS.white};
  gap: 2px;

  @media (max-width: 768px) {
    gap: 0;
  }
`;

export const ImageContainer = styled.div`
  width: fit-content;
  position: relative;
  display: flex;
  flex-direction: column;
  color: ${COLORS.white};
  align-items: center;
  gap: 8px;
  padding: 6px;
  border-radius: 6px;
  background-color: ${COLORS.systemLight50};
`;

export const ImagePreview = styled.img`
  object-fit: contain;
  width: 100%;
  border-radius: 4px;
  background-color: ${COLORS.white};
`;

export const ThumbnailList = styled(List)`
  overflow-x: hidden !important;
  width: calc(var(--thumbnailWidth) + 12px) !important;
`;

export const Title = styled.span`
  font-weight: 700;
  font-size: 16px;
`;

export const SmallText = styled.span`
  font-size: 12px;
`;

export const ChooseLanguageStyled = styled(ChooseLanguageV2)`
  && {
    .ant-select-selection-item {
      color: ${COLORS.white} !important;
    }

    .ant-select-arrow {
      color: ${COLORS.white} !important;
    }
  }
`;
